import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  LinearProgress,
  Typography,
  Stack,
} from '@mui/material';
import {
  Flag as PriorityIcon,
  TrendingUp as UrgencyIcon,
  Calculate as ScoreIcon,
} from '@mui/icons-material';
import {
  TaskPriority,
  TaskUrgency,
  calculateTaskScore,
} from '../utils/taskUtils';

interface TaskPriorityIndicatorProps {
  priority: TaskPriority;
  urgency: TaskUrgency;
  size?: 'small' | 'medium' | 'large';
  showScore?: boolean;
  showDetails?: boolean;
}

const priorityColors: Record<TaskPriority, string> = {
  [TaskPriority.Critical]: '#DC2626',
  [TaskPriority.Highest]: '#EF4444',
  [TaskPriority.High]: '#F59E0B',
  [TaskPriority.Medium]: '#3B82F6',
  [TaskPriority.Low]: '#10B981',
  [TaskPriority.Lowest]: '#94A3B8',
};

const urgencyColors: Record<TaskUrgency, string> = {
  [TaskUrgency.Urgent]: '#EF4444',
  [TaskUrgency.Normal]: '#3B82F6',
  [TaskUrgency.Low]: '#10B981',
};

export const TaskPriorityIndicator: React.FC<TaskPriorityIndicatorProps> = ({
  priority,
  urgency,
  size = 'medium',
  showScore = false,
  showDetails = false,
}) => {
  const score = calculateTaskScore(priority, urgency);
  const maxScore = 63; // Critical (6) * 10 + Urgent (3)
  const scorePercentage = (score / maxScore) * 100;

  const chipSize = size === 'small' ? 'small' : 'medium';
  const iconSize = size === 'small' ? 16 : size === 'medium' ? 20 : 24;

  if (showDetails) {
    return (
      <Box sx={{ minWidth: 200 }}>
        <Stack spacing={1}>
          {/* Priority */}
          <Box display="flex" alignItems="center" gap={1}>
            <PriorityIcon sx={{ fontSize: iconSize, color: priorityColors[priority] }} />
            <Typography variant="body2" fontWeight="medium">
              Priority:
            </Typography>
            <Chip
              label={priority}
              size={chipSize}
              sx={{
                backgroundColor: priorityColors[priority],
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          </Box>

          {/* Urgency */}
          <Box display="flex" alignItems="center" gap={1}>
            <UrgencyIcon sx={{ fontSize: iconSize, color: urgencyColors[urgency] }} />
            <Typography variant="body2" fontWeight="medium">
              Urgency:
            </Typography>
            <Chip
              label={urgency}
              size={chipSize}
              sx={{
                backgroundColor: urgencyColors[urgency],
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          </Box>

          {/* Score */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <ScoreIcon sx={{ fontSize: iconSize }} />
              <Typography variant="body2" fontWeight="medium">
                Priority Score: {score}/{maxScore}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={scorePercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'grey.300',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${priorityColors[TaskPriority.Low]} 0%, ${priorityColors[TaskPriority.High]} 50%, ${priorityColors[TaskPriority.Critical]} 100%)`,
                },
              }}
            />
          </Box>
        </Stack>
      </Box>
    );
  }

  const content = (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Chip
        label={priority}
        size={chipSize}
        icon={<PriorityIcon sx={{ fontSize: iconSize, color: 'white !important' }} />}
        sx={{
          backgroundColor: priorityColors[priority],
          color: 'white',
          fontWeight: 'bold',
        }}
      />
      {urgency !== TaskUrgency.Normal && (
        <Chip
          label={urgency}
          size={chipSize}
          variant="outlined"
          sx={{
            borderColor: urgencyColors[urgency],
            color: urgencyColors[urgency],
            fontWeight: 'bold',
          }}
        />
      )}
      {showScore && (
        <Chip
          label={`Score: ${score}`}
          size={chipSize}
          variant="outlined"
          icon={<ScoreIcon sx={{ fontSize: iconSize }} />}
        />
      )}
    </Box>
  );

  if (showScore || urgency !== TaskUrgency.Normal) {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="body2">Priority: {priority}</Typography>
            <Typography variant="body2">Urgency: {urgency}</Typography>
            <Typography variant="body2" fontWeight="bold">
              Score: {score}/{maxScore}
            </Typography>
          </Box>
        }
        arrow
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

// Priority Matrix Component for visualizing priority vs urgency
interface PriorityMatrixProps {
  tasks?: Array<{
    id: string;
    title: string;
    priority: TaskPriority;
    urgency: TaskUrgency;
  }>;
  onTaskClick?: (taskId: string) => void;
}

export const TaskPriorityMatrix: React.FC<PriorityMatrixProps> = ({
  tasks = [],
  onTaskClick,
}) => {
  const priorityOrder = [
    TaskPriority.Critical,
    TaskPriority.Highest,
    TaskPriority.High,
    TaskPriority.Medium,
    TaskPriority.Low,
    TaskPriority.Lowest,
  ];

  const urgencyOrder = [TaskUrgency.Urgent, TaskUrgency.Normal, TaskUrgency.Low];

  const getTasksInCell = (priority: TaskPriority, urgency: TaskUrgency) => {
    return tasks.filter(task => task.priority === priority && task.urgency === urgency);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Priority vs Urgency Matrix
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: '100px repeat(3, 1fr)', gap: 1 }}>
        {/* Header */}
        <Box />
        {urgencyOrder.map(urgency => (
          <Box key={urgency} sx={{ textAlign: 'center', p: 1 }}>
            <Chip
              label={urgency}
              size="small"
              sx={{
                backgroundColor: urgencyColors[urgency],
                color: 'white',
              }}
            />
          </Box>
        ))}

        {/* Rows */}
        {priorityOrder.map(priority => (
          <React.Fragment key={priority}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              <Chip
                label={priority}
                size="small"
                sx={{
                  backgroundColor: priorityColors[priority],
                  color: 'white',
                  width: '100%',
                }}
              />
            </Box>
            {urgencyOrder.map(urgency => {
              const cellTasks = getTasksInCell(priority, urgency);
              const score = calculateTaskScore(priority, urgency);
              
              return (
                <Box
                  key={`${priority}-${urgency}`}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    minHeight: 60,
                    backgroundColor: `rgba(${
                      priority === TaskPriority.Critical && urgency === TaskUrgency.Urgent
                        ? '220, 38, 38'
                        : priority === TaskPriority.High && urgency === TaskUrgency.Urgent
                        ? '245, 158, 11'
                        : '156, 163, 175'
                    }, 0.1)`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Score: {score}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {cellTasks.map(task => (
                      <Chip
                        key={task.id}
                        label={task.title}
                        size="small"
                        onClick={() => onTaskClick?.(task.id)}
                        clickable={!!onTaskClick}
                        sx={{
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Higher scores indicate tasks that need immediate attention. Critical + Urgent tasks have the highest priority score.
        </Typography>
      </Box>
    </Box>
  );
};